import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Download,
  Loader2,
  Wand2,
  Edit3,
  AlertCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GifSettings,
  GifFrame,
  GifQualityPreset,
  QUALITY_PRESETS,
  DEFAULT_GIF_SETTINGS,
} from '@/features/gif/types';
import { useGifStore } from '@/features/gif/gif-store';

interface VideoToGifDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoSrc: string | null;
  videoName?: string | null;
}

const FPS_OPTIONS = [5, 8, 10, 12, 15, 20, 24, 30];

/**
 * F1: Video-to-GIF Direct Mode
 *
 * Opens a dialog that:
 *  1. Loads the video into an internal <video> element
 *  2. Lets the user pick a start/end time range
 *  3. Extracts frames at the chosen FPS via canvas seeking
 *  4. Encodes them with the existing GIF worker
 *  5. Allows download or "Edit in Advanced Mode" handoff
 */
export function VideoToGifDialog({
  open,
  onOpenChange,
  videoSrc,
  videoName,
}: VideoToGifDialogProps) {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cancelRef = useRef(false);

  const setStoreFrames = useGifStore((s) => s.setFrames);
  const updateStoreSettings = useGifStore((s) => s.updateSettings);

  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState<[number, number]>([0, 0]);
  const [fps, setFps] = useState(10);
  const [quality, setQuality] = useState<GifQualityPreset>('medium');
  const [isProcessing, setIsProcessing] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'extracting' | 'encoding' | 'done'>('idle');
  const [progress, setProgress] = useState(0);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [outputBlob, setOutputBlob] = useState<Blob | null>(null);
  const [extractedFrames, setExtractedFrames] = useState<GifFrame[]>([]);
  const [error, setError] = useState<string | null>(null);

  const maxRangeSec = 60;
  const maxFrames = 200;

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      cancelRef.current = true;
      if (outputUrl) URL.revokeObjectURL(outputUrl);
      setPhase('idle');
      setProgress(0);
      setOutputUrl(null);
      setOutputBlob(null);
      setExtractedFrames([]);
      setError(null);
      setIsProcessing(false);
    } else {
      cancelRef.current = false;
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    setDuration(video.duration);
    const initialEnd = Math.min(video.duration, 3);
    setRange([0, initialEnd]);
  }, []);

  const selectedDuration = Math.max(0, range[1] - range[0]);
  const estimatedFrames = Math.min(
    maxFrames,
    Math.max(1, Math.round(selectedDuration * fps)),
  );
  const willExceedRange = selectedDuration > maxRangeSec;
  const estimatedKb = Math.round(
    estimatedFrames * (QUALITY_PRESETS[quality].maxWidth / 100) * 6,
  );

  const handleStart = async () => {
    setError(null);
    if (!videoRef.current || !canvasRef.current) return;
    if (selectedDuration <= 0) {
      setError('Select a non-empty time range first');
      return;
    }
    if (willExceedRange) {
      setError(
        `Range too long. Max ${maxRangeSec}s for quick GIFs.`,
      );
      return;
    }

    setIsProcessing(true);
    setPhase('extracting');
    setProgress(0);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Canvas not available');
      setIsProcessing(false);
      return;
    }

    // Determine output canvas size
    const sourceW = video.videoWidth || 640;
    const sourceH = video.videoHeight || 360;
    const targetW = Math.min(QUALITY_PRESETS[quality].maxWidth, sourceW);
    const scale = targetW / sourceW;
    const targetH = Math.round(sourceH * scale);
    canvas.width = targetW;
    canvas.height = targetH;

    const frameCount = estimatedFrames;
    const interval = selectedDuration / frameCount;
    const frames: GifFrame[] = [];

    try {
      for (let i = 0; i < frameCount; i++) {
        if (cancelRef.current) {
          setIsProcessing(false);
          setPhase('idle');
          return;
        }
        const t = range[0] + i * interval;
        await seekVideo(video, t);
        ctx.drawImage(video, 0, 0, targetW, targetH);
        const dataUrl = canvas.toDataURL('image/png');
        frames.push({
          id: `qg-${Date.now()}-${i}`,
          sourceFrameId: `qg-${i}`,
          imageData: dataUrl,
          order: i,
          width: targetW,
          height: targetH,
        });
        setProgress(Math.round(((i + 1) / frameCount) * 50)); // first half = extraction
      }

      if (cancelRef.current) {
        setIsProcessing(false);
        setPhase('idle');
        return;
      }

      setExtractedFrames(frames);
      setPhase('encoding');

      // Build encoding settings
      const settings: GifSettings = {
        ...DEFAULT_GIF_SETTINGS,
        frameRate: fps,
        defaultFrameDuration: Math.round(1000 / fps),
        quality,
        maxWidth: QUALITY_PRESETS[quality].maxWidth,
        playbackMode: 'loop',
        loop: 0,
        speedMultiplier: 1,
        watermark: { enabled: false, text: '', opacity: 0 },
      };

      // Spawn worker directly
      const worker = new Worker(
        new URL('@/features/gif/gif-encoder.worker.ts', import.meta.url),
        { type: 'module' },
      );

      const result = await new Promise<{ blob: Blob; dataUrl: string } | null>((resolve, reject) => {
        worker.onmessage = (event) => {
          const msg = event.data;
          if (msg.type === 'progress') {
            // encoding = second half of progress
            setProgress(50 + Math.round(msg.percent / 2));
          } else if (msg.type === 'complete') {
            resolve({ blob: msg.gifBlob, dataUrl: msg.gifDataUrl });
            worker.terminate();
          } else if (msg.type === 'error') {
            reject(new Error(msg.message));
            worker.terminate();
          }
        };
        worker.onerror = (e) => reject(new Error(e.message));
        worker.postMessage({ type: 'encode', frames, settings });
      });

      if (!result || cancelRef.current) {
        setIsProcessing(false);
        setPhase('idle');
        return;
      }

      setOutputBlob(result.blob);
      setOutputUrl(result.dataUrl);
      setProgress(100);
      setPhase('done');
      toast.success('GIF created!', {
        description: `${formatBytes(result.blob.size)} · ${frames.length} frames`,
      });
    } catch (err) {
      console.error('Quick GIF failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to create GIF');
      setPhase('idle');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setIsProcessing(false);
    setPhase('idle');
  };

  const handleDownload = () => {
    if (!outputBlob) return;
    const url = URL.createObjectURL(outputBlob);
    const a = document.createElement('a');
    const baseName = (videoName || 'animation').replace(/\.[^/.]+$/, '');
    a.href = url;
    a.download = `${baseName}-quick.gif`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEditAdvanced = () => {
    if (extractedFrames.length === 0) return;
    // Push frames into the main store and navigate to GIF creator
    setStoreFrames(extractedFrames);
    updateStoreSettings({
      frameRate: fps,
      quality,
      maxWidth: QUALITY_PRESETS[quality].maxWidth,
    });
    onOpenChange(false);
    navigate('/?view=gif');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Quick GIF
          </DialogTitle>
          <DialogDescription>
            Select a time range and create a GIF in seconds.
          </DialogDescription>
        </DialogHeader>

        {/* Hidden video for seeking & off-screen canvas */}
        <video
          ref={videoRef}
          src={videoSrc || undefined}
          onLoadedMetadata={handleLoadedMetadata}
          crossOrigin="anonymous"
          muted
          playsInline
          preload="auto"
          className="hidden"
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* Output preview takes precedence */}
        {phase === 'done' && outputUrl ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-2 flex justify-center">
              <img src={outputUrl} alt="Generated GIF" className="max-h-80 rounded" />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Frames</p>
                <p className="font-semibold">{extractedFrames.length}</p>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Duration</p>
                <p className="font-semibold">{selectedDuration.toFixed(1)}s</p>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Size</p>
                <p className="font-semibold">{outputBlob ? formatBytes(outputBlob.size) : ', '}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleDownload} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Download GIF
              </Button>
              <Button variant="outline" onClick={handleEditAdvanced}>
                <Edit3 className="h-4 w-4 mr-2" />
                Edit in Advanced Mode
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Range selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Time Range</Label>
                <span className="text-xs text-muted-foreground">
                  {formatTime(range[0])} → {formatTime(range[1])} ({selectedDuration.toFixed(1)}s)
                </span>
              </div>
              {duration > 0 ? (
                <Slider
                  value={range}
                  onValueChange={(v) => setRange([v[0], v[1]] as [number, number])}
                  min={0}
                  max={duration}
                  step={0.05}
                  minStepsBetweenThumbs={1}
                  disabled={isProcessing}
                />
              ) : (
                <div className="h-5 rounded bg-muted animate-pulse" />
              )}
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>0:00</span>
                <span>{formatTime(duration)}</span>
              </div>
              {willExceedRange && (
                <p className="mt-1 text-[11px] text-destructive">
                  Range exceeds {maxRangeSec}s limit.
                </p>
              )}
            </div>

            {/* FPS + Quality */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Frame Rate</Label>
                <Select
                  value={String(fps)}
                  onValueChange={(v) => setFps(Number(v))}
                  disabled={isProcessing}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FPS_OPTIONS.map((f) => (
                      <SelectItem key={f} value={String(f)}>
                        {f} FPS
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Quality</Label>
                <Select
                  value={quality}
                  onValueChange={(v) => setQuality(v as GifQualityPreset)}
                  disabled={isProcessing}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low ({QUALITY_PRESETS.low.maxWidth}px)</SelectItem>
                    <SelectItem value="medium">Medium ({QUALITY_PRESETS.medium.maxWidth}px)</SelectItem>
                    <SelectItem value="high">High ({QUALITY_PRESETS.high.maxWidth}px)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/40 p-3 text-center text-xs">
              <div>
                <p className="text-muted-foreground">Frames</p>
                <p className="font-semibold">{estimatedFrames}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Duration</p>
                <p className="font-semibold">{selectedDuration.toFixed(1)}s</p>
              </div>
              <div>
                <p className="text-muted-foreground">Est. Size</p>
                <p className="font-semibold">~{estimatedKb}KB</p>
              </div>
            </div>

            {/* Progress */}
            {isProcessing && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span>{phase === 'extracting' ? 'Extracting frames…' : 'Encoding GIF…'}</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              {isProcessing ? (
                <Button variant="outline" onClick={handleCancel} className="flex-1">
                  Cancel
                </Button>
              ) : (
                <Button
                  onClick={handleStart}
                  disabled={duration === 0 || selectedDuration <= 0 || willExceedRange}
                  className="flex-1"
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Create GIF
                </Button>
              )}
            </div>

          </div>
        )}

        {/* Hidden helper for full-page loading state */}
        {phase === 'extracting' && progress === 0 && (
          <Loader2 className="hidden" />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Seek the video to a target time and wait for `seeked` event (with timeout). */
function seekVideo(video: HTMLVideoElement, time: number, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      video.removeEventListener('seeked', handler);
      video.removeEventListener('error', errorHandler);
    };
    const handler = () => {
      cleanup();
      resolve();
    };
    const errorHandler = () => {
      cleanup();
      reject(new Error('Video error during seek'));
    };
    video.addEventListener('seeked', handler);
    video.addEventListener('error', errorHandler);
    timer = setTimeout(() => {
      cleanup();
      reject(new Error(`Seek timeout after ${timeoutMs}ms at t=${time}s`));
    }, timeoutMs);
    try {
      video.currentTime = time;
    } catch (e) {
      cleanup();
      reject(e instanceof Error ? e : new Error('Seek failed'));
    }
  });
}

function formatTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s % 1) * 100);
  return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default VideoToGifDialog;
